import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import uniqid from 'uniqid';
import { Box } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { AppButton, DropdownSort } from '@Components/Control';
import { SearchResultRow } from './TableComponents';
import {
  renderTableHeader,
  tablePadding,
  NoDataPlaceholder,
  TableWrapper,
  SearchInput,
  FilterDropdown,
} from '../SharedComponents';
import ActiveFilters from './ActiveFilters';

const TableControls = ({
  small,
  displayInputValue = null,
  handleQuery,
  searchQuery,
  handleFilter,
  handleSort,
  useFilters = null,
  listSearchesMeta = null,
  exportButton = null,
}) => {
  if (listSearchesMeta) {
    const sortField = searchQuery?.sort?.name || null;
    const sortDirection = searchQuery?.sort?.direction || 'desc';

    return (
      <Box wrap direction="row" gap="0.5rem" align="center">
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <SearchInput
            key="search"
            small={small}
            handleSearch={(uid, value, predicate, seperateValues, reset) => (
              handleFilter(uid, value, predicate, seperateValues, reset)
            )}
            initialValue={displayInputValue}
          />
        </Box>
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <FilterDropdown
            key={uniqid()}
            small={small}
            selectedFilters={searchQuery.filters || null}
            handleFilter={(uid, value, predicate, seperateValues, reset) => (
              handleFilter(uid, value, predicate, seperateValues, reset)
            )}
            filterOptions={useFilters}
          />
        </Box>
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <DropdownSort
            sortOptions={listSearchesMeta.sort.name.options.map((opt) => ({
              api: opt.name,
              display: opt.formatted,
            }))}
            customPad="0.35rem 0.3rem"
            selectedSortField={sortField}
            sortDirection={sortDirection}
            onSortFieldChange={(field, direction) => handleSort(field, direction)}
            onSortDirectionToggle={() => {
              // Toggle direction and call handleSort with current field
              const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
              handleSort(sortField, newDirection);
            }}
          />
        </Box>
        {Boolean(exportButton) && exportButton}
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <AppButton
            overrideHover
            disabled={(
              Object.entries(searchQuery)
                .filter(([key]) => key !== 'paging')
                .every(([, value]) => !value || (typeof value === 'object' && Object.keys(value).length === 0))
            )}
            onClick={() => handleQuery('reset', null, null)}
            level="tableControls"
            color="var(--button-primary-text)"
            bgColor="var(--button-primary-bg)"
            label="Reset"
          />
        </Box>
      </Box>
    );
  }

  return null;
};

TableControls.propTypes = {
  small: PropTypes.bool.isRequired,
  displayInputValue: PropTypes.string,
  searchQuery: PropTypes.shape({
    paging: PropTypes.shape({
      to: PropTypes.number.isRequired,
      size: PropTypes.number.isRequired,
    }),
    sort: PropTypes.shape({
      name: PropTypes.string.isRequired,
      direction: PropTypes.string.isRequired,
    }),
    filters: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.any,
      predicate: PropTypes.string.isRequired,
    })),
    page: PropTypes.number,
    limit: PropTypes.number,
  }).isRequired,
  handleQuery: PropTypes.func.isRequired,
  handleFilter: PropTypes.func.isRequired,
  handleSort: PropTypes.func.isRequired,
  exportButton: PropTypes.element,
  useFilters: PropTypes.arrayOf(PropTypes.any.isRequired),
  listSearchesMeta: PropTypes.objectOf(PropTypes.any.isRequired),
};

const ListSearchesTable = ({
  small,
  loading,
  listSearchesData = null,
  listSearchesMeta = null,
  searchQuery,
  pageTitle,
  headerMap,
  showViewCount,
  showUnit,
  reviewEnabled,
  handleQuery,
  handleSort = null,
  handleFilter = null,
  handleExportCSV = null,
  submitSearchFeature = null,
  dashboardPath,
  resultsPerPageOptions,
  currentListUid = null,
  paymentPending,
}) => {
  const {
    theme: { page, button, navigation },
    icons,
    statusColorMap,
  } = useAppTheme();
  const searchInputValue = searchQuery?.filters && _.find(searchQuery.filters, ['name', 'name']);
  let displayInputValue = '';
  const isMediumScreen = window.innerWidth <= 975;

  const showFilters = listSearchesMeta && listSearchesMeta.filters;
  let useFilters = null;

  if (showFilters) {
    useFilters = _.filter(listSearchesMeta.filters.options, (({ name }) => name !== 'name' && name !== 'status'));

    if (currentListUid === 'all') {
      useFilters = _.filter(listSearchesMeta.filters.options, (({ name }) => name !== 'name'));
    }
  }

  if (searchInputValue && searchInputValue.value) {
    displayInputValue = searchInputValue.value;
  }

  const currentPage = searchQuery?.paging?.to;
  const resultsPerPage = searchQuery?.paging?.size;

  const handlePagination = (parentKey, updateKey, val) => {
    handleQuery(parentKey, updateKey, val);
  };

  const handleClearAllFilters = () => {
    handleFilter(null, null, null, false, true);
  };

  const renderTableContents = () => {
    if (loading || !listSearchesData || (
      listSearchesData && listSearchesData.data?.searches?.length === 0
    )) {
      const showRefreshButton = listSearchesData
        && listSearchesData.data?.searches?.length === 0
        && searchQuery.filters;

      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={Boolean(showRefreshButton)}
          refreshQuery={() => handleFilter(null, null, null, false, true)}
          buttonHighlight={button.primaryBg}
          loading={loading || !listSearchesData}
          label="No results found for this category"
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box>
        {!small && (
          <Box
            fill="horizontal"
            wrap={false}
            direction="row"
            border={{
              color: navigation.border, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {headerMap.map(({ uid, display, size }) => (
              <Box key={uid} width={size} pad={{ horizontal: '0.5rem', top: '1rem', bottom: '0.5rem' }}>
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        <Box fill="horizontal" direction="column">
          {listSearchesData.data.searches.map((rowData) => (
            <SearchResultRow
              key={uniqid()}
              small={small}
              rowData={rowData}
              statusColorMap={statusColorMap}
              seeDetailsIcon={icons.seeDetailsIcon}
              searchPriorityIcon={icons.searchPriorityIcon}
              primaryText={page.primaryText}
              highlightText={page.altHighlight}
              buttonHighlight={button.primaryBg}
              headerMap={headerMap}
              isMediumScreen={isMediumScreen}
              dashboardPath={dashboardPath}
              submitSearchFeature={submitSearchFeature}
              showViewCount={showViewCount}
              showUnit={showUnit}
              reviewEnabled={reviewEnabled}
            />
          ))}
        </Box>
      </Box>
    );
  };

  const hidePagination = !listSearchesData || (listSearchesData?.data?.searches?.length === 0)
    || loading;

  return (
    <TableWrapper
      small={small}
      header={pageTitle}
      boxPad={tablePadding(small)}
      totalResults={listSearchesData?.data?.totalCount}
      currentResults={listSearchesData?.data?.searches?.length}
      selectedResultsCount={resultsPerPage}
      handleResultsPerPage={(val) => handlePagination('paging', 'size', val)}
      handlePage={(val) => handlePagination('paging', 'to', val)}
      resultsPerPageOptions={resultsPerPageOptions}
      isPaged={listSearchesData?.data?.totalCount > listSearchesData?.data?.searches?.length}
      hidePagination={hidePagination}
      currentPage={currentPage}
      tableControls={(
        <Box direction="row" gap="0.5rem">
          <TableControls
            small={small}
            displayInputValue={displayInputValue}
            searchQuery={searchQuery}
            handleQuery={handleQuery}
            handleFilter={handleFilter}
            handleSort={handleSort}
            useFilters={useFilters}
            listSearchesMeta={listSearchesMeta}
            exportButton={handleExportCSV && (
              <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
                <AppButton
                  overrideHover
                  onClick={() => handleExportCSV()}
                  height="var(--table-controls-height)"
                  label="Export CSV"
                  level="dynamicSmall"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  disabled={paymentPending}
                />
              </Box>
            )}
          />
        </Box>
      )}
    >
      {searchQuery?.filters && searchQuery.filters.length > 0 && (
        <Box pad={{ bottom: 'small' }}>
          <ActiveFilters
            filters={searchQuery.filters}
            onClearAll={handleClearAllFilters}
            primaryText={page.primaryText}
            focusHighlight={page.focusHighlight}
          />
        </Box>
      )}
      {renderTableContents()}
    </TableWrapper>
  );
};

ListSearchesTable.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  showViewCount: PropTypes.bool.isRequired,
  showUnit: PropTypes.bool.isRequired,
  reviewEnabled: PropTypes.bool.isRequired,
  listSearchesData: PropTypes.shape({
    data: PropTypes.shape({
      totalCount: PropTypes.number.isRequired,
      searches: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
        name: PropTypes.string,
        title: PropTypes.string,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        status: PropTypes.string.isRequired,
        priority: PropTypes.string,
        resultCount: PropTypes.number,
        unreviewedCount: PropTypes.number,
        viewCount: PropTypes.number,
        unit: PropTypes.string,
        platforms: PropTypes.arrayOf(PropTypes.string.isRequired),
        features: PropTypes.arrayOf(PropTypes.string.isRequired),
      })).isRequired,
    }),
  }),
  listSearchesMeta: PropTypes.shape({
    sort: PropTypes.shape({
      name: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
      direction: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
    }).isRequired,
    filters: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        formatted: PropTypes.string.isRequired,
        validValues: PropTypes.oneOfType([
          PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string.isRequired,
              formatted: PropTypes.string.isRequired,
            }),
          ),
          PropTypes.arrayOf(PropTypes.string),
        ]),
        validPredicates: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      }).isRequired).isRequired,
    }).isRequired,
  }),
  searchQuery: PropTypes.shape({
    paging: PropTypes.shape({
      to: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
      size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
    }),
    sort: PropTypes.shape({
      name: PropTypes.string.isRequired,
      direction: PropTypes.string.isRequired,
    }),
    filters: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.any,
      predicate: PropTypes.string.isRequired,
    })),
    page: PropTypes.number,
    limit: PropTypes.number,
  }).isRequired,
  pageTitle: PropTypes.string.isRequired,
  currentListUid: PropTypes.string,
  headerMap: PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.string.isRequired,
    uid: PropTypes.string.isRequired,
    size: PropTypes.string.isRequired,
  }).isRequired).isRequired,
  handleQuery: PropTypes.func.isRequired,
  handleSort: PropTypes.func,
  handleFilter: PropTypes.func,
  submitSearchFeature: PropTypes.func,
  handleExportCSV: PropTypes.func,
  dashboardPath: PropTypes.func.isRequired,
  resultsPerPageOptions: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  paymentPending: PropTypes.bool.isRequired,
};

export default ListSearchesTable;
